import { UseFormReturnType } from '@mantine/form';
import { UpsertEntityInput } from '../data-capture/merge-review/useUpsertEntities';
import { DynamicUiData, FieldWithValue, FormValuesType } from '../UI/dynamicUI/types';

const isObject = (value: unknown) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Nested lists the profile edits through their own entity type. The parent still carries a copy
// of the list, and sending that too would re-add items the user deleted from the list.
const FIELDS_OWNED_BY_THEIR_OWN_ENTITY: Record<string, string[]> = {
  EstatePlanning: ['authority_arrangements'],
  TaxProfile: ['utilised_benefits', 'utilised_tax_efficient_scheme', 'utilised_tax_credit'],
};

const withoutFieldsOwnedElsewhere = (entityType: string, values: FieldWithValue) => {
  const ownedElsewhere = FIELDS_OWNED_BY_THEIR_OWN_ENTITY[entityType];
  if (!ownedElsewhere) {
    return values;
  }
  return Object.fromEntries(
    Object.entries(values).filter(([fieldName]) => !ownedElsewhere.includes(fieldName)),
  );
};

export const prepFormValuesForUpsert = (
  formValues: FormValuesType,
  uiData: DynamicUiData,
  form: UseFormReturnType<FormValuesType>,
  preventTouchedFieldsFilter = false,
) => {
  return Object.keys(formValues)
    .filter(
      entityType =>
        preventTouchedFieldsFilter || (form.isDirty(entityType) && !!uiData?.entities[entityType]),
    )
    .map((entityType): UpsertEntityInput | UpsertEntityInput[] => {
      const entityTypeValues = formValues[entityType];
      if (
        isObject(entityTypeValues) &&
        formValues[entityType] &&
        !Array.isArray(entityTypeValues)
      ) {
        return {
          entityFieldsMetadata: uiData.ui.entitiesMetadata[entityType],
          entityType: entityType,
          values: withoutFieldsOwnedElsewhere(entityType, entityTypeValues),
        };
      }

      if (Array.isArray(entityTypeValues)) {
        return {
          entityFieldsMetadata: uiData.ui.entitiesMetadata[entityType],
          entityType: entityType,
          values: entityTypeValues.map(
            (_, index) => (formValues[entityType] as FieldWithValue[])[index],
          ),
        };
      }

      throw Error('Unexpected data format during form submission');
    })
    .flatMap(e => e);
};
